import React, { useState, ReactElement } from 'react';
import { useRouter } from 'next/router';
import { css } from '@emotion/react';
import Image from 'next/image';
import { WindowDimension } from '@/utils/WindowDemension';
import { copyClipboard, sendKakaoLinkShare } from '@/utils/linkshare';

import { IOutput, IResponse } from '@/types/crime';

interface Iprops {
  data: IResponse;
  userEmail?: string;
}

interface copyTexts {
  clipboard: string;
  kakao: string;
}

const mobileCss = `width: 100%; font-size:16px;`;

const renderCssProps = `display: flex;
justify-content: center;
align-items: center;
flex-flow: row wrap;
width: 80%;
min-height: 5rem;
border: 1px solid #c4c4c4;
border-radius: 5px;
color: #6f6f6f;
font-size: 18px;
gap: 1rem;
@media (max-width: 992px) {
  ${mobileCss}
}
`;

export const CrimeResult = ({ data, userEmail }: Iprops): ReactElement => {
  const [copyText, setCopyText] = useState<copyTexts>({
    clipboard: 'URL 복사',
    kakao: '카카오톡',
  });

  const router = useRouter();
  const { width } = WindowDimension();

  const kakaoData = () => {
    const asPath = router.asPath;
    const email = userEmail || '';

    let input = '';

    if (data.input.length === 0) {
      input = '문제가 된 상황에 맞춰 무죄 판결문을 찾아드립니다.';
    } else {
      input = `사건 내용: "${data.input}"`;
    }

    const result = {
      title: '무슨 범죄인지 찾아드립니다',
      description: input,
      path: asPath,
      email: email,
    };

    return result;
  };

  const handleCopy = async () => {
    await copyClipboard();
    {
      width < 992 ? alert('복사되었습니다.') : null;
    }
    setCopyText({ ...copyText, clipboard: '복사 완료' });
    setTimeout(() => {
      setCopyText({ ...copyText, clipboard: 'URL 복사' });
    }, 3000);
  };

  const resultTag = (output: IOutput[]) => {
    const result = output.map((ele) => {
      return (
        <React.Fragment key={ele.value}>
          <div
            css={css`
              border: 2px solid #d93729;
              box-sizing: border-box;
              border-radius: 17px;
              padding: 3px 15px;
              color: #d93729;
              font-size: 18px;
              font-weight: 700;
              @media (max-width: 992px) {
                ${mobileCss}
                width: fit-content;
                margin: 0.5rem 0;
              }
            `}
          >
            {ele.value}
          </div>
        </React.Fragment>
      );
    });

    return result;
  };

  const render = (data: IResponse): JSX.Element => {
    const { output } = data;

    if (output.length === 0) {
      return (
        <div
          css={css`
            ${renderCssProps}
          `}
        >{`현재 검색한 죄명이 없습니다.`}</div>
      );
    }

    const tags = resultTag(output);

    return (
      <div
        css={css`
          ${renderCssProps}
          justify-content: flex-start;
          padding: 1rem;
        `}
      >
        {tags}
      </div>
    );
  };

  return (
    <>
      <div
        css={css`
          font-size: 22px;
          font-weight: 700;
          color: #000000;
          width: 80%;
          display: flex;
          flex-flow: row nowrap;
          justify-content: space-between;
          align-items: space-between;
          gap: 1rem;
          @media (max-width: 992px) {
            ${mobileCss}
          }
        `}
      >
        <div
          css={css`
            display: flex;
            flex-flow: row nowrap;
            justify-content: center;
            align-items: center;
            gap: 1rem;
          `}
        >
          <Image
            src='/crime/crime_2.png'
            layout='fixed'
            alt={'Legal Engine'}
            width={25}
            height={25}
          />
          {`AI 검색 결과`}
        </div>
        <div
          css={css`
            display: flex;
            flex-flow: row nowrap;
            justify-content: center;
            align-items: center;
            gap: 0.75rem;
            font-size: 18px;
            font-weight: 700;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-flow: row nowrap;
              justify-content: center;
              align-items: center;
              gap: 0.5rem;
              cursor: pointer;
            `}
            onClick={handleCopy}
          >
            <Image src='/social/link.png' layout='fixed' alt={'link'} width={32} height={32} />
            <span
              css={css`
                @media (max-width: 992px) {
                  display: none;
                }
              `}
            >
              {copyText.clipboard}
            </span>
          </div>
          <div
            css={css`
              display: flex;
              flex-flow: row nowrap;
              justify-content: center;
              align-items: center;
              gap: 0.5rem;
              cursor: pointer;
            `}
            onClick={() => sendKakaoLinkShare(kakaoData())}
          >
            <Image
              src='/social/kakao_navy.png'
              objectFit='fill'
              alt={'kakaolink'}
              width={32}
              height={32}
            />
            <span
              css={css`
                @media (max-width: 992px) {
                  display: none;
                }
              `}
            >
              {copyText.kakao}
            </span>
          </div>
        </div>
      </div>
      {render(data)}
      <div
        css={css`
          width: 80%;
          padding-left: 0.75rem;
          text-indent: -1rem;
          color: #9b9b9b;
          font-size: 18px;
          @media (max-width: 992px) {
            ${mobileCss}
          }
        `}
      >
        {`※ 리걸엔진은 판결문 검색 서비스만 제공할 뿐, 법률상담이나 예측 등 법률사무를 하지 않습니다.`}
        <br />
        {`검색 결과는 단순 참고용으로만 사용하시고, 정확한 상담은 변호사에 문의하시기 바랍니다.`}
      </div>
    </>
  );
};
